import React from 'react';
import { connect } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import { NamedLink } from '../../components';
import { buildActionRows, mostUrgentRow, hasUrgentAction } from './actionCenter';
import { deriveActionInput, isSellerAccount } from './dashboardSelectors';

import css from './SellerActionReminder.module.css';

/**
 * The one-line reminder that follows a seller around (P25009-385 BR-4 / AC-3):
 * "While any urgent item is open, a short one-line reminder appears at the top of
 * every seller page."
 *
 * It shows the single most urgent row, never a list — the point is a nudge back
 * to the overview, not a second copy of the to-do list on every page. Only `red`
 * rows qualify, so unread messages and low stock never nag; they wait on the
 * overview.
 *
 * Renders nothing at all unless there is something genuinely urgent, so it is
 * safe to mount unconditionally in the seller nav.
 */
export const SellerActionReminderComponent = ({ rows, isSeller, currentPage }) => {
  if (!isSeller) return null;
  if (!hasUrgentAction(rows)) return null;

  /* On the overview itself the full list is already on screen. Repeating the top
   * row directly above it would be pure duplication. */
  if (currentPage === 'SellerDashboardPage') return null;

  const row = mostUrgentRow(rows);
  if (!row) return null;

  /* Outer band paints a full-width OPAQUE strip; inner caps the content at
   * contentMaxWidth. Both halves matter because this sits inside the sticky
   * topbar: a translucent background let scrolling page content show through the
   * banner, and a background capped to the content width left transparent gaps
   * either side of it on wide screens. Same split UserNav uses, for the same
   * reason. */
  return (
    <div className={css.bar} role="status">
      <div className={css.inner}>
        <span className={css.dot} aria-hidden="true" />
        <span className={css.text}>
          <FormattedMessage id={row.titleKey} />
        </span>
        <NamedLink name="SellerDashboardPage" className={css.link}>
          <FormattedMessage id="SellerActionReminder.link" />
        </NamedLink>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  rows: buildActionRows(deriveActionInput(state)),
  isSeller: isSellerAccount(state.user?.currentUser),
});

export default connect(mapStateToProps)(SellerActionReminderComponent);
